import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";

interface PendingAccount {
  user_id: string;
  email: string;
  full_name: string | null;
  created_at: string;
  provider: string | null;
}

const UserApprovals = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState<PendingAccount[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  const isCeo = profile?.role === "CEO";

  const fetchPending = useCallback(async () => {
    setLoading(true);
    const { data, error } = await (supabase as any).rpc("pending_accounts");
    if (error) {
      toast({ title: "Não foi possível carregar as contas", description: error.message, variant: "destructive" });
    }
    setAccounts((data || []) as PendingAccount[]);
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    if (isCeo) fetchPending();
  }, [isCeo, fetchPending]);

  const decide = async (account: PendingAccount, status: "ACTIVE" | "INACTIVE") => {
    setBusy(account.user_id);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .update({ status } as any)
        .eq("user_id", account.user_id)
        .select("status")
        .single();
      if (error) throw error;
      if ((data as any)?.status !== status) throw new Error("A alteração não foi aceita.");
      toast({ title: status === "ACTIVE" ? "Conta aprovada" : "Conta recusada", description: account.email });
      fetchPending();
    } catch (err) {
      toast({ title: "Não foi possível concluir", description: (err as Error).message, variant: "destructive" });
    } finally {
      setBusy(null);
    }
  };

  if (!isCeo) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <p className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-600">Você não tem permissão para aprovar contas.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 xl:p-6">
      <header>
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9a7f52]">Acessos</p>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">Aprovação de contas</h1>
        <p className="mt-1 text-sm text-slate-500">Quem cria uma conta só entra no sistema depois da sua aprovação.</p>
      </header>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-[#9a7f52]" /></div>
        ) : accounts.length === 0 ? (
          <p className="px-6 py-10 text-center text-sm text-slate-500">Nenhuma conta aguardando aprovação.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {accounts.map((account) => (
              <li key={account.user_id} className="flex flex-col gap-3 p-4 xl:flex-row xl:items-center xl:justify-between xl:px-5">
                <div className="min-w-0">
                  <p className="break-words text-[15px] font-semibold text-slate-900">{account.email}</p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {account.full_name || "Sem nome informado"} · Criada em {new Date(account.created_at).toLocaleDateString("pt-BR")} · {account.provider === "google" ? "Entrou pelo Google" : "Entrou com e-mail e senha"}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button size="sm" disabled={busy !== null} onClick={() => decide(account, "ACTIVE")} className="rounded-xl bg-slate-900 text-white hover:bg-slate-800">
                    <Check className="mr-1.5 h-4 w-4 text-[#e8d5a8]" />Aprovar
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => decide(account, "INACTIVE")} className="rounded-xl">
                    <X className="mr-1.5 h-4 w-4" />Recusar
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default UserApprovals;
